import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar, Tag } from 'lucide-react';
import { getBlogArticle } from '../lib/api';
import { Button } from '../components/ui/button';

const BlogArticlePage = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const data = await getBlogArticle(slug);
        setArticle(data);
      } catch (error) {
        console.error('Error fetching article:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchArticle();
  }, [slug]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  // Simple markdown-like rendering
  const renderContent = (content) => {
    if (!content) return null;
    
    const lines = content.split('\n');
    const elements = [];
    let currentList = [];
    let inList = false;

    lines.forEach((line, index) => {
      const trimmedLine = line.trim();
      
      if (trimmedLine.startsWith('# ')) {
        if (inList) {
          elements.push(<ul key={`list-${index}`} className="list-disc pl-6 mb-6 space-y-2">{currentList}</ul>);
          currentList = [];
          inList = false;
        }
        elements.push(
          <h1 key={index} className="font-outfit text-3xl lg:text-4xl font-bold text-ghrb-black mb-6 mt-8">
            {trimmedLine.substring(2)}
          </h1>
        );
      } else if (trimmedLine.startsWith('## ')) {
        if (inList) {
          elements.push(<ul key={`list-${index}`} className="list-disc pl-6 mb-6 space-y-2">{currentList}</ul>);
          currentList = [];
          inList = false;
        }
        elements.push(
          <h2 key={index} className="font-outfit text-2xl font-bold text-ghrb-black mb-4 mt-8">
            {trimmedLine.substring(3)}
          </h2>
        );
      } else if (trimmedLine.startsWith('### ')) {
        if (inList) {
          elements.push(<ul key={`list-${index}`} className="list-disc pl-6 mb-6 space-y-2">{currentList}</ul>);
          currentList = [];
          inList = false;
        }
        elements.push(
          <h3 key={index} className="font-outfit text-xl font-semibold text-ghrb-black mb-3 mt-6">
            {trimmedLine.substring(4)}
          </h3>
        );
      } else if (trimmedLine.startsWith('- ') || trimmedLine.startsWith('* ')) {
        inList = true;
        const listContent = trimmedLine.substring(2);
        currentList.push(
          <li key={index} className="font-jakarta text-ghrb-gray-dark">
            {listContent.replace(/\*\*(.*?)\*\*/g, '<strong class="text-ghrb-black font-semibold">$1</strong>')}
          </li>
        );
      } else if (trimmedLine.match(/^\d+\. /)) {
        inList = true;
        const listContent = trimmedLine.replace(/^\d+\. /, '');
        currentList.push(
          <li key={index} className="font-jakarta text-ghrb-gray-dark">
            {listContent}
          </li>
        );
      } else if (trimmedLine === '') {
        if (inList) {
          elements.push(<ul key={`list-${index}`} className="list-disc pl-6 mb-6 space-y-2">{currentList}</ul>);
          currentList = [];
          inList = false;
        }
      } else {
        if (inList) {
          elements.push(<ul key={`list-${index}`} className="list-disc pl-6 mb-6 space-y-2">{currentList}</ul>);
          currentList = [];
          inList = false;
        }
        // Handle bold text
        const processedLine = trimmedLine.replace(/\*\*(.*?)\*\*/g, '<strong class="text-ghrb-black font-semibold">$1</strong>');
        elements.push(
          <p key={index} className="font-jakarta text-ghrb-gray-dark leading-relaxed mb-4" dangerouslySetInnerHTML={{ __html: processedLine }} />
        );
      }
    });

    if (inList && currentList.length > 0) {
      elements.push(<ul key="final-list" className="list-disc pl-6 mb-6 space-y-2">{currentList}</ul>);
    }

    return elements;
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-ghrb-orange border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-outfit text-2xl font-bold mb-4">Article non trouvé</h1>
          <Link to="/journal">
            <Button>Retour au journal</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="blog-article-page" className="min-h-screen bg-white">
      {/* Back link */}
      <div className="bg-ghrb-gray py-4">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link 
            to="/journal"
            className="inline-flex items-center font-jakarta text-sm text-ghrb-gray-dark hover:text-ghrb-orange transition-colors"
            data-testid="back-to-blog"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour au journal
          </Link>
        </div>
      </div>

      {/* Article Header */}
      <motion.section 
        className="py-12 lg:py-16 bg-ghrb-gray"
        initial="hidden"
        animate="visible"
        variants={fadeInUp}
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-6">
            <span className="font-outfit text-xs uppercase tracking-[0.15em] text-ghrb-orange">
              {article.category}
            </span>
            <span className="w-1 h-1 bg-ghrb-gray-dark rounded-full"></span>
            <span className="font-jakarta text-sm text-ghrb-gray-dark flex items-center gap-1.5">
              <Calendar className="w-4 h-4" />
              {formatDate(article.created_at)}
            </span>
          </div>
          
          <h1 
            className="font-outfit text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-ghrb-black mb-6"
            data-testid="article-title"
          >
            {article.title}
          </h1>
          
          <p className="font-jakarta text-lg text-ghrb-gray-dark">
            {article.excerpt}
          </p>

          {article.tags?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-6">
              {article.tags.map((tag, index) => (
                <span 
                  key={index}
                  className="inline-flex items-center gap-1.5 px-3 py-1 bg-white rounded-full font-jakarta text-xs text-ghrb-gray-dark"
                >
                  <Tag className="w-3 h-3" />
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
      </motion.section>

      {/* Cover Image */}
      <motion.div 
        className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 -mt-4"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <div className="aspect-[21/9] rounded-3xl overflow-hidden">
          <img
            src={article.cover_image}
            alt={article.title}
            className="w-full h-full object-cover"
            data-testid="article-cover-image"
          />
        </div>
      </motion.div>

      {/* Article Content */}
      <motion.section 
        className="py-12 lg:py-16"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
      >
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="blog-content" data-testid="article-content">
            {renderContent(article.content)}
          </div>
        </div>
      </motion.section>

      {/* CTA */}
      <section className="py-12 lg:py-16 bg-ghrb-gray">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="font-outfit text-2xl font-bold text-ghrb-black mb-4">
            Découvrez nos produits
          </h3>
          <p className="font-jakarta text-ghrb-gray-dark mb-6">
            Des solutions simples et efficaces pour votre quotidien.
          </p>
          <Link to="/produits">
            <Button 
              size="lg"
              className="bg-ghrb-orange hover:bg-ghrb-orange-hover text-white font-jakarta font-semibold px-8 py-6 rounded-full"
              data-testid="article-cta"
            >
              Voir les produits
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default BlogArticlePage;
